/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect } from "react";
import localforage from "localforage";
import { enterpriseAPI, notifyAPI, RTA } from "../..";
import { setMockNegotiationMode } from "../../services/negotiationBridge";
import { SelectedCourse, Tutorial } from "../tutorial/Tutorial";
import { courses } from "../tutorial/courses";
import { AxiosRequestConfig } from "axios";
import { NotifyToastItems } from "../NotifyToast";
import { sessionInfo } from "../../utils/utils";
import { isDaKasaCnpj } from "../../utils/dakasaOrg";

const UserContext = createContext<any>(null);
const NotificationContext = createContext<any>(null);

export const useUser = () => useContext(UserContext);
export const useNotification = () => useContext(NotificationContext);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [avatar, setAvatar] = useState("");
  const [orgAvatar, setOrgAvatar] = useState("");
  const [orgName, setOrgName] = useState("");
  // DaKasa's own company, known only from its CNPJ. False until
  // /get-organization answers with it, and on any failure.
  const [isDaKasaOrg, setIsDaKasaOrg] = useState(false);
  const [amaryCourse, setCourseState] = useState<SelectedCourse | undefined>(undefined);
  const courseRef = React.useRef<SelectedCourse>();
  const mountedRef = React.useRef(true);
  const setAmaryCourse = React.useCallback<
    React.Dispatch<React.SetStateAction<SelectedCourse | undefined>>
  >(
    (selection) => {
      if (!mountedRef.current) return;
      const next = typeof selection === "function" ? selection(courseRef.current) : selection;
      const switchesMode = Boolean(next) !== Boolean(courseRef.current);
      courseRef.current = next;
      if (switchesMode) {
        // Admission must close at selection, before React effects or session
        // storage reads. The bridge owns initialization and stale-request fencing.
        void setMockNegotiationMode(Boolean(next), {
          orgAvatar: orgAvatar || avatar || undefined,
          orgName: orgName || undefined,
        }).catch(() => {});
      }
      setCourseState(next);
    },
    [orgAvatar, avatar, orgName],
  );
  const [orgIsOnlyForAdults, setOrgIsOnlyForAdults] = useState(false);
  const [concludedTutorials, setConcludedTutorials] = useState<string[]>([]);

  async function getConcludedCourses() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers: { [key: string]: string | undefined } = {
      "X-JWT": JWT,
    };

    try {
      const tutorials = await enterpriseAPI.get("/get-tutorials-status", {
        headers,
      });
      setConcludedTutorials(tutorials.data);
      // In development, auto-skip courses marked with skipInDev unless the
      // dev explicitly opts out via REACT_APP_DEV_SKIP_TUTORIALS=false.
      const devSkipEnabled =
        process.env.NODE_ENV === "development" &&
        process.env.REACT_APP_DEV_SKIP_TUTORIALS !== "false";
      if (devSkipEnabled) {
        const devSkipIds = Object.values(courses)
          .filter((c) => c.summary.skipInDev)
          .map((c) => c.summary.id);
        if (devSkipIds.length > 0) {
          setConcludedTutorials((prev: string[]) => [...new Set([...prev, ...devSkipIds])]);
        }
      }
    } catch (error) {
      console.error("Error fetching concluded tutorials:", error);
    }
  }

  async function fetchUserAvatar() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return;

    const headers: { [key: string]: string | undefined } = { "X-JWT": JWT };

    try {
      const result = await enterpriseAPI.get("/get-collaborator", { headers });
      const data = result.data;
      if (data.avatar) {
        setAvatar(data.avatar);
      }
      if (data.orgAvatar) {
        setOrgAvatar(data.orgAvatar);
      }

      const orgResult = await enterpriseAPI.get("/is-org-only-for-adults", {
        headers,
      });
      const orgData = orgResult.data;
      setOrgIsOnlyForAdults(orgData);

      try {
        const orgInfo = await enterpriseAPI.get("/get-organization", {
          headers,
        });
        const orgPayload = orgInfo.data;
        if (orgPayload?.avatar) {
          setOrgAvatar(orgPayload.avatar);
        }
        if (orgPayload?.name) {
          setOrgName(orgPayload.name);
        }
        setIsDaKasaOrg(isDaKasaCnpj(orgPayload?.cnpj));
      } catch (error) {
        setIsDaKasaOrg(false);
        console.error("Error fetching organization avatar", error);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchUserAvatar();
    getConcludedCourses();
  }, []);

  useEffect(() => {
    if (amaryCourse) {
      localforage.setItem("inTutorial", true);
      return;
    }

    localforage.removeItem("inTutorial");
  }, [amaryCourse]);

  useEffect(() => {
    // Course presence is handled synchronously by its setter. Metadata updates
    // stay here and do not reset an already initialized mode.
    void setMockNegotiationMode(Boolean(courseRef.current), {
      orgAvatar: orgAvatar || avatar || undefined,
      orgName: orgName || undefined,
    }).catch(() => {});
  }, [orgAvatar, avatar, orgName]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (courseRef.current) void setMockNegotiationMode(false).catch(() => {});
    };
  }, []);

  return (
    <UserContext.Provider
      value={{
        avatar,
        setAvatar,
        fetchUserAvatar,
        orgIsOnlyForAdults,
        setOrgIsOnlyForAdults,
        isDaKasaOrg,
        amaryCourse,
        setAmaryCourse,
        concludedTutorials,
        setConcludedTutorials,
      }}
    >
      <Tutorial
        courseSelected={amaryCourse}
        onClose={() => {
          localforage.removeItem("inTutorial");
          setAmaryCourse(undefined);
        }}
        onNext={() =>
          setAmaryCourse((prev) => {
            if (!prev) return undefined;
            const nextChapter = prev.currentChapter + 1;
            return { id: prev.id, currentChapter: nextChapter };
          })
        }
      />
      {children}
    </UserContext.Provider>
  );
};

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<NotifyToastItems[]>([]);

  const hasMoreNotifications = React.useRef<boolean>(true);
  const pageNotificationsRef = React.useRef<number>(0);
  const sizeNotifications = 20;

  class SocketEvents {
    static receiveNotification(payload: NotifyToastItems) {
      setNotifications((prevNotifications) => {
        if (prevNotifications.find((n) => n.id === payload.id)) {
          return prevNotifications;
        }
        return [payload, ...prevNotifications];
      });
    }
  }

  async function fetchNotifications() {
    const JWT = (await localforage.getItem("JWT")) as string | undefined;
    if (!JWT) return false;

    const headers = {
      "X-JWT": JWT,
    };

    const options: AxiosRequestConfig = {
      headers,
      params: {
        page: pageNotificationsRef.current,
        size: sizeNotifications,
      },
    };

    try {
      const response = await notifyAPI.get("/list", options);
      const fetchedNotifications: NotifyToastItems[] = response.data || [];
      if (fetchedNotifications.length === 0) {
        hasMoreNotifications.current = false;
        return false;
      }

      setNotifications((prev) => [...prev, ...fetchedNotifications]);
      pageNotificationsRef.current += 1;
      return true;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      return false;
    }
  }

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    RTA.connect();
    sessionInfo().then((session) => {
      if (!session) return;
      RTA.on(`notifyReceive-${session.org_id}-${session.iss}`, SocketEvents.receiveNotification);
    });
    return () => {
      RTA.disconnect();
    };
  }, []);

  // Sweep expired notifications so derived UI (golden badge, unread count,
  // toast list) updates the moment a notification's expiresAt passes — without
  // requiring a page reload.
  useEffect(() => {
    const tick = setInterval(() => {
      setNotifications((prev) => {
        const now = Date.now();
        const live = prev.filter((n) => !n.expiresAt || new Date(n.expiresAt).getTime() > now);
        return live.length === prev.length ? prev : live;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        fetchNotifications,
        hasMoreNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
